// Core packages
import { formatDate } from '@angular/common';
import {
  Component, ElementRef, Input, OnDestroy, OnInit, SimpleChange,
  ViewChild
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { MatDialog } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subject, Subscription } from "rxjs";
import {
  finalize, startWith, take,
  takeUntil, tap
} from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { CondoStructuresService } from "../../condo-structures.service";
import MappingIndex from "../mapping-index.interface";
// import { PropertyDetailMappingNoteDialogComponent } from "../../components/mapping-note-dialog/mapping-note-dialog.component";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { PrintQrcodeDialogComponent } from 'src/app/modules/companies/components/print-qrcode-dialog/print-qrcode-dialog.component';
import { ExportContractsDialogComponent } from 'src/app/modules/properties/components/export-contracts-dialog/export-contracts-dialog.component';
import { ExportRasDocsDialogComponent } from 'src/app/modules/properties/components/export-ras-docs-dialog/export-ras-docs-dialog.component';
import { openDocumentArchiveExportContractsDialog } from "src/app/modules/properties/components/document-archive-export-contracts-dialog/document-archive-export-contracts-dialog.component";
import { openDocumentArchiveExportRasDialog } from "src/app/modules/properties/components/document-archive-export-ras-dialog/document-archive-export-ras-dialog.component";
import { DocumentArchiveExportDownloadService } from "src/app/modules/properties/document-archive-export-download.service";
import {
  DocumentArchiveExportMenu,
  DocumentArchiveExportTarget,
  documentArchiveExportTargetOf,
  hasDocumentArchiveExportMenu,
  legacyPageExportMenu,
} from "src/app/modules/properties/document-archive-export.utils";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { AddCommunicationDialogComponent } from "src/app/shared/components/dialogs/add-communication-dialog/add-communication-dialog.component";
import { ICompany } from "src/app/shared/models/company/company.interface";
import { PropertyDetailDocumentNoteDialogComponent } from "../../components/document-note-dialog/document-note-dialog.component";
import { PropertyDetailMappingDeleteDialogComponent } from "../../components/mapping-delete-dialog/mapping-delete-dialog.component";
import { PropertyDetailMappingRemakeDialogComponent } from "../../components/mapping-remake-dialog/mapping-remake-dialog.component";
import {
  MappingArchiveDocsDialogComponent,
  MappingArchiveDocsDialogData,
  MappingArchiveDocsDialogMode,
  MappingArchiveDocsDialogResult,
} from "../../components/mapping-archive-docs-dialog/mapping-archive-docs-dialog.component";
import { remakePlantSelection } from "../../mapping-archive-docs.utils";
import { isArchiveV3Live } from "src/app/shared/utils/archive-switch.utils";
import { DocumentArchivesService } from "../../document-archives.service";
import { PropertyEditNoteDialogComponent } from "../document-edit-note-dialog/document-edit-note-dialog.component";
import { PropertyDetailMappingViewDialogComponent } from "../mapping-view-dialog/mapping-view-dialog.component";

/**
 * Script start
 */
@Component({
  selector: "app-property-detail-mapping-tab",
  templateUrl: "./mapping-tab.component.html",
  styleUrls: ["./mapping-tab.component.scss"]
})
export class PropertyDetailMappingTabComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  onDestroy$ = new Subject<void>();
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserToleType = "";
  loggedUserShared = false;
  loggedUserTemporary = false;
  loggedUserMaster = false;
  loggedUserAdmin = false;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  // Could not be "Property" type because in "get" method in the back-end
  // We are populating some fields, so the model is not the same!
  @Input() property: PropertyInterface;
  @Input() condoStructure: any;
  @Input() formActiveSection: string;
  @Input() formActiveSubitemId: string;
  cardIsLoading = true;
  mappingIndex: MappingIndex[] = [];
  @ViewChild("card", { static: false }) card: ElementRef;
  @ViewChild("panelContainer", { static: false }) panelContainer: ElementRef<any>;
  formIsLoading = false; // Used to view/hide form loading spinner
  // formItems: any[] = [];
  activeItem: MappingIndex;
  activeSubitem: any;
  // BEGIN - Formly config
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {
    formState: {
      disabled: false
    }
  };
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: true,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Formly config

  loggedUserRoleType = "";

  existingNotes: any[] = [];
  enableFormModify = false;
  sectionPlant = false;
  propertyId: string;
  documentArchive: any;
  remakePlant = false;
  remakePlantDocs: any = {};
  rimappatura = false;
  mappaturaOpen = false;
  previousFormValue: any;
  showSideMenu = true;

  subscriptionType = 'FREE';

  /**
   * `subscription.key` of the property for the rules of the «Esporta» menu once the switch is live:
   * `""` for a property with no plan, which the legacy treats as a paid one, `null` until the mapping
   * has loaded it, together with `subscriptionType`.
   */
  exportPlanKey: string | null = null;

  /** The Excel being downloaded from the new archive, `null` when none (@see exportFromArchiveV3) */
  exportingTarget: DocumentArchiveExportTarget | null = null;

  /** The download of that Excel, cancelled when the tab closes */
  private exportSubscription: Subscription | null = null;

  condoStructureTemplateId = "";
  condoStructureTemplate: any[] = [];

  notesExist = false;

  mappingQrcodeData: string;

  companyData: ICompany;

  /**
   * The entries of the «Esporta» menu as far as the switch decides them: every one, under the rules
   * of the template, until the switch; once it is live, those of the «Esporta» menu of the new
   * dossier, whose routes they then call (@see legacyPageExportMenu).
   *
   * @since 3.0.0
   */
  get downloadMenu(): DocumentArchiveExportMenu {
    return legacyPageExportMenu(isArchiveV3Live(), {
      roleType: this.loggedUserRoleType,
      permissions: this.loggedUserPermissions,
      subscriptionKey: this.exportPlanKey,
    });
  }

  /**
   * Whether the «Esporta» button is shown to the roles of the template: always until the switch, as
   * before; once it is live, only with at least one entry, as in the new dossier.
   *
   * @since 3.0.0
   */
  get downloadMenuShown(): boolean {
    return !isArchiveV3Live() || hasDocumentArchiveExportMenu(this.downloadMenu);
  }

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private propertiesService: PropertiesService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private condoStructuresService: CondoStructuresService,
    private documentArchivesService: DocumentArchivesService,
    private settingsService: SettingsService,
    private companiesService: CompaniesService,
    private documentArchiveExportDownloadService: DocumentArchiveExportDownloadService,
  ) {

  }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    this.mappingQrcodeData = `${this.configService.frontEndHost}/qrcode-admin-mapping/${this.condoStructure._id}`;
    
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => {
        this.loggedUser = res;
        const currentRole = this.loggedUser.userRoles.find((obj: any) => {
          return (
            obj._id.toString() ===
            this.authService.loggedUserRequestedRole$.getValue().toString()
          );
        });
        this.loggedUserRoleType = currentRole.role.type;
      })
    );

    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );
    
    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    if (this.condoStructure) {
      for (const key in this.condoStructure) {
        if (
          Array.isArray(this.condoStructure[key]) &&
          this.condoStructure[key].length
        ) {
          for (const el of this.condoStructure[key]) {
            if (el.note && Array.isArray(el.note) && el.note.length) {
              this.notesExist = true;
              break;
            }
          }
          if (this.notesExist) {
            break;
          }
        }
      }
    }

    if (this.property) {
      this.documentArchivesService
        .get(this.property.documentArchive)
        .subscribe(res => {
          if (res.status) {
            this.documentArchive = res.data;
          }
          // this.cardIsLoading = false;
        });

      if (
        this.property.admin && 
        this.property.admin.company && 
        this.property.admin.company._id
      ) {
        this.companiesService
          .get(this.property.admin.company._id)
          .subscribe(res => {
            if (res.status) {
              this.companyData = res.data;
            } else {
              this.toastrService.error(res.message);
            }
          });
      }
    }

  }

  /**
   * Listen on input values changes
   *
   * @since 1.0.0
   */
  ngOnChanges(changes: SimpleChange): void {
    const property = changes["property"];

    // Update property
    if (property && property.currentValue && property.currentValue._id) {
      this.property = property.currentValue;
      this.propertyId = property.currentValue._id;
    }

    // Update form section depending on current mappingFormActiveSection
    if (this.property) {
      // @NEW
      this.subscriptions.push(
        this.authService.loggedUserRequestedRoleType$.subscribe(
          res => {
            this.loggedUserToleType = res;
            if (this.loggedUserToleType === "master"
              || this.loggedUserToleType === "collaborator") {
              this.loggedUserMaster = true;
              this.rimappatura = false;
              if (this.condoStructure.rimappatura) {
                this.rimappatura = true;
                this.mappaturaOpen = this.condoStructure.open;
              }
            } else if (this.loggedUserToleType === "admin"
              || this.loggedUserToleType === "operator") {
              this.loggedUserAdmin = true;
              this.rimappatura = true;
              this.mappaturaOpen = this.condoStructure.open;
            }
            if (this.loggedUserToleType === "shared") {
              this.loggedUserShared = true;
            }
            if (this.loggedUserToleType === "temporary") {
              this.loggedUserTemporary = true;
            }
          }
        )
      );

      if (this.mappingIndex.length === 0) {
        this.subscriptions.push(
          this.settingsService.getStructure().subscribe(
            (res) => {
              if (res.status) {
                if (
                  res.condoStructureTemplate &&
                  Array.isArray(res.condoStructureTemplate) &&
                  res.condoStructureTemplate.length > 0 &&
                  res.condoStructureTemplate[0].area &&
                  Array.isArray(res.condoStructureTemplate[0].area)
                ) {
                  this.condoStructureTemplateId = res.condoStructureTemplate[0]._id;
                  this.condoStructureTemplate = res.condoStructureTemplate[0].area;

                  const structureTemplate = JSON.parse(JSON.stringify(this.condoStructureTemplate));

                  // hide question deleted
                  this.mappingIndex = structureTemplate.filter((el: any) => {
                    if (!el.deleted) {
                      if (el.content && Array.isArray(el.content)) {
                        const content = el.content.filter((subEl: any) => !subEl.deleted);
                        if (content && Array.isArray(content)) {
                          content.forEach((subContent: any) => {
                            if (
                              subContent && 
                              subContent.formConfig &&
                              Array.isArray(subContent.formConfig) &&
                              subContent.formConfig.length === 1 && 
                              subContent.formConfig[0].fieldGroup &&
                              Array.isArray(subContent.formConfig[0].fieldGroup)
                            ) {
                              const fieldGroup = subContent.formConfig[0].fieldGroup.filter((group: any) => !group.deleted);
                              subContent.formConfig[0].fieldGroup = fieldGroup;
                            }
                          });
                        }
                        el.content = content;
                      }
                      return true;
                    }
                  });

                  this.mappingIndex = this.translateKeys2(
                    ["placeholder", "label"],
                    this.mappingIndex
                  );

                  this.updateFormSection();
                  this.cardIsLoading = false;

                  // Check property type: Condo or BSC
                  this.subscriptionType = this.property.subscription.key;
                  // The plan as the rules of the exports read it once the switch is live
                  this.exportPlanKey = typeof this.subscriptionType === "string" ? this.subscriptionType : "";

                  if (this.property.type === "bsc") {
                    const strutture = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.strutture";
                    });
                    if (strutture
                      && strutture.content
                      && Array.isArray(strutture.content)
                    ) {
                      let index = strutture.content.findIndex((item: any) => {
                        return item.key === "condominiBsc"
                      });
                      strutture.content[index].hidden = false;
                      index = strutture.content.findIndex((item: any) => {
                        return item.key === "fabbricatoAbitativo"
                      });
                      strutture.content[index].hidden = true;
                      index = strutture.content.findIndex((item: any) => {
                        return item.key === "struttureFabbindip"
                      });
                      strutture.content[index].hidden = false;
                    }

                    const trattamentoAcqua = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.trattamentoAcqua";
                    });
                    if (trattamentoAcqua
                      && trattamentoAcqua.content
                      && Array.isArray(trattamentoAcqua.content)
                    ) {
                      const index = trattamentoAcqua.content.findIndex((item: any) => {
                        return item.key === "sottostazioneAltroCondBsc"
                      });
                      trattamentoAcqua.content[index].hidden = true;
                    }

                    const riscaldamentoCondizionamento = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.riscaldamentoCondizionamento";
                    });
                    if (riscaldamentoCondizionamento
                      && riscaldamentoCondizionamento.content
                      && Array.isArray(riscaldamentoCondizionamento.content)
                    ) {
                      // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                      //   return item.key === "sottostazioniCt"
                      // });
                      // riscaldamentoCondizionamento.content[index].hidden = true;
                      // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                      //   return item.key === "centraleRicervimento"
                      // });
                      // riscaldamentoCondizionamento.content[index].hidden = true;
                      const index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                        return item.key === "cannaCollettiva"
                      });
                      riscaldamentoCondizionamento.content[index].hidden = true;
                    }

                    const dispositiviAntincendio = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.dispositiviAntincendio";
                    });
                    if (dispositiviAntincendio
                      && dispositiviAntincendio.content
                      && Array.isArray(dispositiviAntincendio.content)
                    ) {
                      const index = dispositiviAntincendio.content.findIndex((item: any) => {
                        return item.key === "evac"
                      });
                      dispositiviAntincendio.content[index].hidden = true;
                    }
                  } else {
                    const strutture = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.strutture";
                    });
                    if (strutture
                      && strutture.content
                      && Array.isArray(strutture.content)
                    ) {
                      let index = strutture.content.findIndex((item: any) => {
                        return item.key === "condominiBsc"
                      });
                      strutture.content[index].hidden = true;
                      index = strutture.content.findIndex((item: any) => {
                        return item.key === "fabbricatoAbitativo"
                      });
                      strutture.content[index].hidden = false;
                      index = strutture.content.findIndex((item: any) => {
                        return item.key === "struttureFabbindip"
                      });
                      strutture.content[index].hidden = false;
                    }

                    const trattamentoAcqua = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.trattamentoAcqua";
                    });
                    if (trattamentoAcqua
                      && trattamentoAcqua.content
                      && Array.isArray(trattamentoAcqua.content)
                    ) {
                      const index = trattamentoAcqua.content.findIndex((item: any) => {
                        return item.key === "sottostazioneAltroCondBsc"
                      });
                      trattamentoAcqua.content[index].hidden = false;
                    }

                    const riscaldamentoCondizionamento = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.riscaldamentoCondizionamento";
                    });
                    if (riscaldamentoCondizionamento
                      && riscaldamentoCondizionamento.content
                      && Array.isArray(riscaldamentoCondizionamento.content)
                    ) {
                      // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                      //   return item.key === "sottostazioniCt"
                      // });
                      // riscaldamentoCondizionamento.content[index].hidden = false;
                      // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                      //   return item.key === "centraleRicervimento"
                      // });
                      // riscaldamentoCondizionamento.content[index].hidden = false;
                      const index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                        return item.key === "cannaCollettiva"
                      });
                      riscaldamentoCondizionamento.content[index].hidden = false;
                    }

                    const dispositiviAntincendio = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.dispositiviAntincendio";
                    });
                    if (dispositiviAntincendio
                      && dispositiviAntincendio.content
                      && Array.isArray(dispositiviAntincendio.content)
                    ) {
                      const index = dispositiviAntincendio.content.findIndex((item: any) => {
                        return item.key === "evac"
                      });
                      dispositiviAntincendio.content[index].hidden = false;
                    }
                  }

                  this.watchFormChanges();
                  this.watchScaleChanges();
                  this.watchBruciatoriChanges();

                  // Enable fields if we're adding a new element, else disable form
                  if (this.form && this.activeItem
                    && (!this.formActiveSubitemId || this.activeItem.key === "anagrafica")
                    && this.mappaturaOpen
                  ) {
                    this.options.formState = {
                      disabled: false
                    };
                    this.formBtnOptions.disabled = false;
                    this.enableFormModify = true;

                    // Check new element and remake plant, then
                    // get save docs to import in new plant
                    if (this.formActiveSection && !this.formActiveSubitemId) {
                      this.remakePlantDocs = this.condoStructuresService.getRemakeStrucutureDocs();
                      // Check if same plant then remake plant.
                      if (this.remakePlantDocs
                        && this.remakePlantDocs.activeSection &&
                        this.remakePlantDocs.activeSection === this.formActiveSection) {
                        this.remakePlant = true;
                      } else {
                        this.remakePlant = false;
                        this.condoStructuresService.setRemakeStrucutureDocs("", "", {});
                      }
                    }
                    if (typeof this.model.files === "undefined") {
                      this.model["files"] = [];
                    }
                  } else if (this.form && this.formActiveSubitemId) {
                    this.options.formState = {
                      disabled: true
                    };
                    this.formBtnOptions.disabled = true;
                    this.enableFormModify = false;
                  }



                }
              }
            })
        );
      } else {
        this.updateFormSection();
        this.cardIsLoading = false;
      }
      // @OLD
      // this.updateFormSection();
    }
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.exportSubscription) {
      this.exportSubscription.unsubscribe();
    }
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Init components
   *
   * @since 1.0.0
   */


  /**
   * Listen on form values changes
   *
   * @since 1.0.0
   */
  watchFormChanges(): void {
    this.subscriptions.push(this.form.valueChanges.subscribe(
      res => {
        if (res && JSON.stringify(res) === this.previousFormValue) {
          return;
        } else {
          this.previousFormValue = JSON.stringify(res);
          if (this.enableFormModify) {
            this.options.formState = {
              disabled: false,
            };
            this.form.updateValueAndValidity();
          }
          return;
        }
      }
    ));
  }

  /**
   * Listen on input values changes
   *
   * @since 1.0.0
   */
  watchScaleChanges(): void {
    this.mappingIndex = this.mappingIndex.map(
      (item: any): MappingIndex => {
        // Our goal here is to add a event listener to the item we are searching for
        if (
          item.title === "properties.structures.strutture" &&
          Array.isArray(item.content)
        ) {
          item.content.map((subItem: any) => {
            if (
              subItem.key === "fabbricatoAbitativo" &&
              subItem.formConfig &&
              subItem.formConfig.length &&
              subItem.formConfig[0].fieldGroup
            ) {
              const field = subItem.formConfig[0].fieldGroup.find(
                el => el.key === "nscale"
              );
              if (field) {
                field.hooks = {
                  onInit: (field: any): void => {
                    field.formControl.valueChanges
                      .pipe(
                        takeUntil(this.onDestroy$),
                        startWith(field.formControl.value),
                        tap(value => {
                          if (typeof value === "undefined") {
                            value = null;
                          }
                          if (value > field.templateOptions.max) {
                            value = field.templateOptions.max;
                          }
                          if (this.model.scale) {
                            this.model.scale.length = value;
                          }
                          this.model = {
                            ...this.model,
                            nscale: value
                          };
                        })
                      )
                      .subscribe();
                  }
                };
              }
            }
            return subItem;
          });
        }
        return item;
      }
    );
  }

  /**
   * Listen on input values changes
   *
   * @since 1.0.0
   */
  watchBruciatoriChanges(): void {
    this.mappingIndex = this.mappingIndex.map(
      (item: any): MappingIndex => {
        // Our goal here is to add a event listener to the item we are searching for
        if (
          item.title === "properties.structures.riscaldamentoCondizionamento" &&
          Array.isArray(item.content)
        ) {
          item.content.map((subItem: any) => {
            if (
              subItem.key === "centraleTermica" &&
              subItem.formConfig &&
              subItem.formConfig.length &&
              subItem.formConfig[0].fieldGroup
            ) {
              const field = subItem.formConfig[0].fieldGroup.find(
                el => el.key === "numeroBruciatori"
              );
              if (field) {
                field.hooks = {
                  onInit: (field: any): void => {
                    field.formControl.valueChanges
                      .pipe(
                        takeUntil(this.onDestroy$),
                        startWith(field.formControl.value),
                        tap(value => {
                          if (typeof value === "undefined") {
                            value = null;
                          }
                          if (value > field.templateOptions.max) {
                            value = field.templateOptions.max;
                          }
                          this.model.bruciatori.length = value;
                          this.model = {
                            ...this.model,
                            numeroBruciatori: value
                          };
                        })
                      )
                      .subscribe();
                  }
                };
              }
            }
            return subItem;
          });
        }
        return item;
      }
    );
  }

  /**
   * Translate all keys with given labels inside given object and returns it
   *
   * @since 4.0.0
   */
  translateKeys(keys: string[], obj: any): any {
    if (Array.isArray(obj)) {
      obj.forEach((item: MappingIndex) => {
        for (const prop in item) {
          if (item.hasOwnProperty(prop)) {
            if (keys.includes(prop)) {
              item[prop] = this.translateService.instant(item[prop]);
            }
            if (typeof item[prop] === "object") {
              item[prop] = this.translateKeys(keys, item[prop]);
            }
          }
        }
      });
    } else {
      for (const prop in obj) {
        if (obj.hasOwnProperty(prop)) {
          if (keys.includes(prop) && obj[prop]) {
            obj[prop] = this.translateService.instant(obj[prop]);
          }
          if (typeof obj[prop] === "object") {
            obj[prop] = this.translateKeys(keys, obj[prop]);
          }
        }
      }
    }
    return obj;
  }
  // @NEW
  // hooks: {
  //   onInit(field: any) {
  //     field.defaultValue = `Scala ${+field.parent.key + 1}`; // formArray index + 1
  //   },
  // },
  // hooks: {
  //   onInit(field: any) {
  //     field.defaultValue = `Bruciatore ${
  //       +field.parent.key + 1
  //     }`; // formArray index + 1
  //   },
  // },
  translateKeys2(keys: string[], obj: any): any {
    if (Array.isArray(obj)) {
      obj.forEach((item: MappingIndex) => {
        for (const prop in item) {
          // hooks: 
          // 1) numerazione progressiva all'aggiunta delle scale del Fabbricato abitativo
          // 2) numerazione progressiva all'aggiunta dei bruciatori di Centrale termica
          if (prop === "hooks") {
            const propKey = item[prop];
            item[prop] = {
              onInit(field: any) {
                field.defaultValue = `${propKey} ${+field.parent.key + 1}`;
              },
            };
          }
          // hideExpression: 
          // nasconde il corrente campo se il valore del campo indicato soddisfa i criteri indicati da "operator"
          // e diabilita l'obbligatorietà del campo in "templateOptions.required"
          if (prop === "hideExpression" && item[prop]) {
            const propKey = item[prop].key;
            const propCompareValue = item[prop].value;
            if (item[prop].operator === "!==") {
              item[prop] = (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ): boolean => {
                const key = propKey;
                const currentValue = model[key];
                const compareValue = propCompareValue;
                return currentValue !== compareValue;
              }
            } else if (item[prop].operator === "!") {
              item[prop] = (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ): boolean => {
                const key = propKey;
                const currentValue = model[key];
                return !currentValue;
              }
            } else if (item[prop].operator === "!includes") {
              item[prop] = (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ): boolean => {
                const key = propKey;
                const currentValue = model[key];
                const compareValue = propCompareValue;
                return typeof currentValue === "undefined" || !currentValue.includes(compareValue);
              }
            }
          }
          // expressionProperties:
          // Contatore energia elettrica:
          // disabilita il campo "connessioni" e/o "alimenta un singolo impianto" 
          // usato in "templateOptions.disabled"
          if (prop === "expressionProperties" && item[prop]) {
            let propDisabled = item[prop].disabled;
            if (
              propDisabled !== "formState.disabled" &&
              propDisabled !== "connessioni" &&
              propDisabled !== ""
            ) {
              const key = propDisabled;
              propDisabled = (model: any, formState: any, field: FormlyFieldConfig) => {
                return model[key] || formState.disabled;
              };
            } else if (propDisabled === "connessioni") {
              const key = propDisabled;
              propDisabled = (model: any, formState: any, field: FormlyFieldConfig) => {
                if (model[key] === "undefined") {
                  return false;
                } else if (Array.isArray(model[key]) && model[key].length > 0) {
                  return true;
                }
                return false;
              };
            }
            if (typeof item[prop].required !== "undefined") {
              const propKey = item[prop].required.key;
              const propCompareValue = item[prop].required.value;
              if (item[prop].required.operator === "===") {
                item[prop] = {
                  "templateOptions.disabled": propDisabled,
                  "templateOptions.required": (model: any, formState: any, field: FormlyFieldConfig) => {
                    const key = propKey;
                    const currentValue = model[key];
                    const compareValue = propCompareValue;
                    return currentValue === compareValue;
                  }
                }
              } else if (item[prop].required.operator === "!") {
                item[prop] = {
                  "templateOptions.disabled": propDisabled,
                  "templateOptions.required": (model: any, formState: any, field: FormlyFieldConfig) => {
                    const key = propKey;
                    const currentValue = model[key];
                    return !currentValue;
                  }
                }
              } else if (item[prop].required.operator === "") {
                item[prop] = {
                  "templateOptions.disabled": propDisabled,
                  "templateOptions.required": (model: any, formState: any, field: FormlyFieldConfig) => {
                    const key = propKey;
                    const currentValue = model[key];
                    return currentValue;
                  }
                }
              }
            } else {
              item[prop] = {
                "templateOptions.disabled": propDisabled,
              }
            }
          }
          // defaultValue:
          // contiene il valore predefinito del campo corrente
          if (prop === "defaultValue" && item[prop]) {
            if (item[prop] === "false") {
              item[prop] = false;
            } else if (item[prop] === "true") {
              item[prop] = true;
            }
          }
          // wrappers:
          // struttura del titolo/campo nel form: orizzontale/verticale  
          // oppure in assenza di "key" e in presenza di "template" => visualizza contenuto di "template"
          if (prop === "wrappers" && item[prop] && Array.isArray(item[prop]) && item[prop].length === 0) {
            item[prop] = "";
          }
          if (item.hasOwnProperty(prop)) {
            if (keys.includes(prop)) {
              if (item["translation"]) {
                item[prop] = item["translation"].it;
              }
            }
            if (typeof item[prop] === "object") {
              item[prop] = this.translateKeys2(keys, item[prop]);
            }
          }
        }
      });
    } else {
      for (const prop in obj) {
        if (obj.hasOwnProperty(prop)) {
          // options:
          // in "templateOptions" e
          // contiene l'elenco dei valori del campo di tipo select (value/translation)
          if (prop === "options" && Array.isArray(obj[prop])) {
            obj[prop].forEach((elem: any) => {
              if (elem.value && elem.value === "true") {
                elem.value = true;
              } else if (elem.value && elem.value === "false") {
                elem.value = false;
              }
            })
          }
          if (keys.includes(prop) && obj[prop]) {
            obj[prop] = this.translateService.instant(obj[prop]);
            if (prop === "label" && obj["translationLabel"]) {
              obj[prop] = obj["translationLabel"].it;
            } else if (prop === "placeholder" && obj["translationPlaceholder"]) {
              obj[prop] = obj["translationPlaceholder"].it;
            }
          }
          if (typeof obj[prop] === "object") {
            obj[prop] = this.translateKeys2(keys, obj[prop]);
          }
        }
      }
    }
    return obj;
  }

  /**
   * Get children (existing values) for requested section
   *
   * @since 1.0.0
   */
  getChildren(key: any): any[] {
    if (typeof this.condoStructure !== "undefined"
      && typeof this.condoStructure[key] !== "undefined") {
      if (key === "anagrafica") {
        return [];
      } else {
        if (Array.isArray(this.condoStructure[key])) {
          let structures = JSON.parse(
            JSON.stringify(this.condoStructure[key])
          );
          structures = structures.filter(el => el.active);
          return structures;
        } else {
          return [];
        }
      }
    }
    return [];
  }

  /**
   * Update form section depending on route params
   *
   * @since 1.0.0
   */
  updateFormSection(): void {
    // Reset model to prevent import previous form data
    if (this.model) {
      for (const property in this.model) {
        delete this.model[property];
      }
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });

    // Reset data and call the new data form
    if (this.form) {
      this.options.formState = {
        disabled: true
      };
    }

    // Add section param to routing, which is used in connections type
    const params = Object.assign({}, this.activatedRoute.snapshot.params, { selected: false });
    params.mappingFormActiveSection = this.formActiveSection;
    this.activatedRoute.snapshot.params = params;

    if (this.formActiveSection) {
      this.formIsLoading = true; // Start form loading

      // BEGIN - Set open status to current mapping index
      const origFormActiveSection = this.formActiveSection.replace(
        this.property.structure,
        ":structureId"
      );

      this.mappingIndex.map((item: MappingIndex) => {
        item.content.map((subItem: MappingIndex) => {
          if (subItem.section === origFormActiveSection) {
            // Get currente element is a Plant
            // delete note field from form
            // if (subItem.formConfig
            //   && Array.isArray(subItem.formConfig)
            //   && subItem.formConfig.length) {
            //   if (subItem.formConfig[0].fieldGroup
            //     && Array.isArray(subItem.formConfig[0].fieldGroup)
            //     && subItem.formConfig[0].fieldGroup.length) {
            //     const index = subItem.formConfig[0].fieldGroup.findIndex((el: any) => {
            //       return el.key === "note";
            //     })
            //     if (index > -1) {
            //       subItem.formConfig[0].fieldGroup.splice(index, 1);
            //     }
            //   }
            // }
            if (
              item.title === "properties.structures.impiantiElettrici" ||
              item.title === "properties.structures.trattamentoAcqua" ||
              item.title ===
              "properties.structures.riscaldamentoCondizionamento" ||
              item.title === "properties.structures.dispositiviAntincendio" ||
              item.title === "properties.structures.ascensoriEMontauto" ||
              item.title === "properties.structures.impiantiElettronici" ||
              item.title === "properties.structures.lineeVita" ||
              item.title === "properties.structures.impiantiSpeciali" ||
              item.title === "properties.structures.impiantiRicreativi"
            ) {
              this.sectionPlant = true;
            } else {
              this.sectionPlant = false;
            }


            item.expanded = true;
            subItem.expanded = true;
            subItem.active = true;
            this.activeItem = subItem;
            if (
              this.formActiveSection &&
              origFormActiveSection !==
              "condo-structures/:structureId/anagrafica"
            ) {
              const children = this.getChildren(subItem.key);
              if (children.length) {
                this.activeSubitem = children.find(
                  (val: any) => val._id === this.formActiveSubitemId
                );

                if (this.activeSubitem && this.activeSubitem.files) {
                  if (this.form.controls.files) {
                    this.form.controls.files.setValue(this.activeSubitem.files);
                  }
                  this.form.updateValueAndValidity();
                }
                if (this.activeSubitem && this.activeSubitem.note) {
                  this.existingNotes = this.activeSubitem.note;
                  this.activeSubitem.note = null;
                }
                this.fields = subItem.formConfig;

                if (this.activeSubitem) {
                  this.model = Object.assign({ subscription: {} }, this.activeSubitem);
                } else {
                  // Add new subitem. Default name from section
                  const tot = children.length + 1;
                  const nome = `${subItem.translation.it} ${tot}`;
                    // this.translateService.instant(subItem.title) + " " + tot;
                  this.model = { nome, files: [], subscription: {} };

                  // Delete defaultValue from field Files, to clear previous upload image
                  if (subItem.formConfig
                    && Array.isArray(subItem.formConfig)
                    && subItem.formConfig.length) {
                    if (subItem.formConfig[0].fieldGroup
                      && Array.isArray(subItem.formConfig[0].fieldGroup)
                      && subItem.formConfig[0].fieldGroup.length) {
                      const index = subItem.formConfig[0].fieldGroup.findIndex((el: any) => {
                        return el.key === "files";
                      })
                      if (index > -1) {
                        if (subItem.formConfig[0].fieldGroup[index].templateOptions
                          && Array.isArray(subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue)
                          && subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue.length) {
                          subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue = [];
                        }
                      }
                    }
                  }
                }
              } else {
                if (this.activeSubitem && this.activeSubitem.note) {
                  this.existingNotes = this.activeSubitem.note;
                  this.activeSubitem.note = null;
                }
                // New first subitem. Default name from section
                this.fields = subItem.formConfig;
                const nome = `${subItem.translation.it} 1`;
                  // this.translateService.instant(subItem.title) + " 1";
                this.model = { nome, files: [], subscription: {} };

                // Delete defaultValue from field Files, to clear previous upload image
                if (subItem.formConfig
                  && Array.isArray(subItem.formConfig)
                  && subItem.formConfig.length) {
                  if (subItem.formConfig[0].fieldGroup
                    && Array.isArray(subItem.formConfig[0].fieldGroup)
                    && subItem.formConfig[0].fieldGroup.length) {
                    const index = subItem.formConfig[0].fieldGroup.findIndex((el: any) => {
                      return el.key === "files";
                    })
                    if (index > -1) {
                      if (subItem.formConfig[0].fieldGroup[index].templateOptions
                        && Array.isArray(subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue)
                        && subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue.length) {
                        subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue = [];
                      }
                    }
                  }
                }
              }
            } else if (
              subItem.section === "condo-structures/:structureId/anagrafica"
            ) {
              this.activeSubitem = this.condoStructure.anagrafica;
              // Anagrafica: is not completed
              if (typeof this.activeSubitem === "undefined") {
                this.activeSubitem = {
                  active: true
                };
              }
              if (this.activeSubitem && this.activeSubitem.note) {
                this.existingNotes = this.activeSubitem.note;
                this.activeSubitem.note = null;
              }
              this.fields = subItem.formConfig;
              this.model = Object.assign({ subscription: {} }, this.activeSubitem);
              this.formActiveSubitemId = Math.random().toString();
            }
          } else {
            subItem.active = false;
          }
        });
      });

      if (this.form && !this.condoStructure.open) {
        this.options.formState = {
          disabled: true
        };
      }
      // END - Set open status to current mapping index

      setTimeout(() => {
        this.formIsLoading = false;
      }, 300);

    } else {
      // Reset the last item.expanded mappingIndex to close tree from previous structure
      if (Array.isArray(this.mappingIndex) && this.mappingIndex.length) {
        this.mappingIndex.forEach((item: any) => {
          if (item.expanded) {
            item.expanded = false;
            if (Array.isArray(item.content) && item.content.length) {
              item.content.forEach((elem: any) => {
                if (elem.active) {
                  elem.active = false;
                }
              });
            }
          }
        });
      }
    }
  }

  /**
   * Navigate to given section inside the same property detail page
   *
   * @since 1.0.0
   */
  activateSection(rawSection: string): void {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });

    // Return if we're trying to navigate to 'anagrafica' but
    // we're already in that route
    if (rawSection.endsWith('anagrafica')) {
      if (
        this.activatedRoute.snapshot.params.mappingFormActiveSection
        && this.activatedRoute.snapshot.params.mappingFormActiveSection.endsWith('anagrafica')
      ) {
        return;
      }
    }

    const section = rawSection.replace(":structureId", this.property.structure);

    // Already on this section's "new element" form: the router ignores a navigation
    // to the URL it is already on, so the form would not be rebuilt. Removing its
    // controls here would leave the fields on screen but detached from `form`, and
    // Formly re-attaches only the fields that later change visibility: the POST
    // would carry just those (e.g. only `superficiemqr3`) or nothing at all
    if (!this.formActiveSubitemId && this.formActiveSection === section) {
      return;
    }

    // Reset data and call the new data form
    this.formActiveSubitemId = undefined;

    const controls = this.form.controls;
    for (const key in controls) {
      this.form.removeControl(key);
    }

    this.formActiveSubitemId = "";
    this.formActiveSection = section;

    this.router.navigate(
      [this.lang, "app", "properties", this.property._id, "mapping", section],
      {
        replaceUrl: true,
        // queryParams: { refresh: new Date().getTime() }
      }
    );
  }

  /**
   * Handle panel header open/close
   *
   * @since 1.0.0
   *
   */
  onPanelClicked(item: any, index: number): void {
    item.expanded = !item.expanded;
    this.mappingIndex.forEach((el: MappingIndex, i: number) => {
      if (i !== index) {
        el.expanded = false;
      }
    });

    // Handle scroll to panel
    if (item.expanded) {
      const headerHeight = 60;
      const top = (headerHeight * (index + 1)) - headerHeight;
      window.scrollTo({
        top,
        behavior: "smooth"
      });
    }
  }

  /**
   * Handle form item click and open view/edit popup
   *
   * @since 1.0.0
   */
  onFormItemClick(rawSection: string, item: any): void {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });

    let itemId = "";
    if (item && item._id) {
      itemId = item._id;
    }

    const section = rawSection.replace(":structureId", this.property.structure);

    if (rawSection !== 'condo-structures/:structureId/anagrafica') {
      this.enableFormModify = false;
    }

    // @todo: this causes unwanted page reload (page content disappears)
    this.router.navigate(
      [
        this.lang,
        "app",
        "properties",
        this.property._id,
        "mapping",
        section,
        itemId
      ],
      {
        replaceUrl: true
      }
    );

    // if (this.form.dirty && !this.form.pristine) {
    //   const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
    //     minWidth: '50%',
    //     maxHeight: '98vh',
    //     data: {
    //       title: this.translateService.instant('common.warnings.warning'),
    //       message: this.translateService.instant('common.warnings.unsavedData'),
    //       btnOkText: this.translateService.instant('common.cta.continue'),
    //       btnCancelText: this.translateService.instant('common.cta.undo'),
    //       // btnCancelIcon: 'fa-times-circle',
    //     },
    //     disableClose: true,
    //   });
    //   // Subscribe to dialog result (only for 1 emit thanks to "take()")
    //   confirmDialogRef.beforeClosed()
    //     .pipe(
    //       take(1)
    //     )
    //     .subscribe(
    //       res => {
    //         if (!res) {
    //           return;
    //         }

    //         window.scrollTo({
    //           top: 0,
    //           behavior: "smooth"
    //         });

    //         const section = rawSection.replace(":structureId", this.property.structure);
    //         this.enableFormModify = false;
    //         this.router.navigate(
    //           [
    //             this.lang,
    //             "app",
    //             "properties",
    //             this.property._id,
    //             "mapping",
    //             section,
    //             item._id
    //           ],
    //           {
    //             replaceUrl: true
    //           }
    //         );

    //       }
    //     );
    //   return;
    // }
    // // @todo would be nice to check if current form has been changed and not saved and, if so, just block the navigation
    // setTimeout(() => {
    //   const headerOffset = 70;

    //   const bodyRect = document.body.getBoundingClientRect().top;
    //   const elementRect = this.card.nativeElement.getBoundingClientRect().top;
    //   const elementPosition = elementRect - bodyRect;
    //   const offsetPosition = elementPosition - headerOffset;

    //   window.scrollTo({
    //     top: offsetPosition,
    //     behavior: "smooth"
    //   });

    //   const section = rawSection.replace(
    //     ":structureId",
    //     this.property.structure
    //   );

    //   // Navigate to the new route
    //   this.router.navigate([
    //     this.lang,
    //     "app",
    //     "properties",
    //     this.property._id,
    //     "mapping",
    //     section,
    //     item._id
    //   ]);
    // }, 50);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any, newItem: boolean): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    // Update existing item
    if (
      (!newItem && this.model._id) ||
      this.activeItem.section === 'condo-structures/:structureId/anagrafica'
    ) {
      this.subscriptions.push(
        this.condoStructuresService
          .updateSection(
            this.activeItem.section,
            this.property.structure,
            this.model._id,
            this.form.value,
            this.mappaturaOpen
          )
          .pipe(finalize(() => (this.formBtnOptions.active = false)))
          .subscribe(
            res => {
              if (res.status) {
                this.toastrService.success(res.message);

                // Disable form
                if (this.form && this.activeItem.key !== 'anagrafica') {
                  this.options.formState = {
                    disabled: true
                  };
                  this.formBtnOptions.disabled = true;
                  this.enableFormModify = false;
                }

                if (model.note) {
                  const newNoteData = {
                    text: model.note,
                    createdAt: new Date(),
                    createdBy: this.loggedUser._id
                  };
                  this.existingNotes.push(newNoteData);
                  this.model.note = '';
                  const index = this.fields[0].fieldGroup.findIndex((el: any) => {
                    return el.key == 'note';
                  });
                  this.fields[0].fieldGroup[index].formControl.setValue('');
                }
                if (this.model.files) {
                  this.model.files = res.files;
                  if (
                    this.condoStructure &&
                    this.condoStructure[this.activeItem.key] &&
                    Array.isArray(this.condoStructure[this.activeItem.key])) {
                    const structureIndex = this.condoStructure[this.activeItem.key].findIndex((el: any) => el._id.toString() === this.formActiveSubitemId.toString());
                    if (structureIndex > -1) {
                      this.condoStructure[this.activeItem.key][structureIndex].files = res.files;
                    }
                  }
                }
                this.onFormItemClick(this.activeItem.section, res.item);
              }
            },
            err => {
              // Choose one of the following error handling method.
              // 1) First one show a message right under the form fields
              //    (if the form is properly setted)
              // 2) Second one is used to show a mat-error under each field
              //    in the dynamic form (is meant to be used only in dynamicForm)
              // 3) Third one show toastr notifications for each error
              // 4) Fourth one is used to show mat-error under each field
              //    in the ngx-formly form (is mean to be used only with ngx-formly)
              // this.helperService.handleFormError(form, err);
              // this.helperService.handleDynamicFormError(form, err);
              // this.helperService.handleError(err);
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    } else {
      const sendData = this.form.value;
      if (this.remakePlant) {
        sendData["remakePlant"] = true;
        sendData["remakePlantDocs"] = this.remakePlantDocs;
      }
      this.subscriptions.push(
        this.condoStructuresService
          .createSectionItem(
            this.activeItem.section,
            this.property.structure,
            sendData
          )
          .pipe(finalize(() => (this.formBtnOptions.active = false)))
          .subscribe(
            res => {
              if (res.status) {
                this.toastrService.success(res.message);
                if (this.remakePlant) {
                  this.condoStructuresService.setRemakeStrucutureDocs("", "", {});
                }
                // @todo:
                // 1) Resettare il form
                // 2) Pushare elemento creato (con dati derivanti da BE perché cosi abbiamo l'ID) negli elementi esistenti
                // 3) Fare redirect alla route di edit/view dell'elemento appena creato

                // condo-structures/:structureId/fabbricato-abitativo
                // onFormItemClick(rawSection: string, item: any)
                // item._id
                this.condoStructure[this.activeItem.key].push(res.item);
                if (res.item && res.item._id && res.item.structureId) {
                  res.item._id = res.item.structureId;
                }
                this.onFormItemClick(this.activeItem.section, res.item);
              } else {
                // @todo gestire errore! Maybe just a toaster.error
              }
            },
            err => {
              // Choose one of the following error handling method.
              // 1) First one show a message right under the form fields
              //    (if the form is properly setted)
              // 2) Second one is used to show a mat-error under each field
              //    in the dynamic form (is meant to be used only in dynamicForm)
              // 3) Third one show toastr notifications for each error
              // 4) Fourth one is used to show mat-error under each field
              //    in the ngx-formly form (is mean to be used only with ngx-formly)
              // this.helperService.handleFormError(form, err);
              // this.helperService.handleDynamicFormError(form, err);
              // this.helperService.handleError(err);
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    }
  }

  /**
   * Check if given macr-section item is visibile or not depending on
   * structure open status. If structure is open, than this will always returns true
   * If it's false, this will check if at least one sub-section has one or more
   * values. If so, return true, otherwise return false;
   *
   * @since 1.0.0
   */
  checkVisibility(item: any): boolean {
    if (!this.mappaturaOpen) {
      if (item.content && item.content.length) {
        // Let's make anagrafica section ALWAYS visible
        if (item.content[0].key === "anagrafica") {
          return true;
        }
        return item.content.some(
          (subitem: MappingIndex) => this.getChildren(subitem.key).length
        );
      }
      return false;
    }
    return true;
  }

  /**
   * Handle click on "open" or "close" mapping buttons
   *
   * @since 1.0.0
   */
  onToggleStructure(action: string): void {
    const message =
      action === "open"
        ? this.translateService.instant(
          "properties.detail.mappingTab.confirmDialog.confirmOpening"
        )
        : this.translateService.instant(
          "properties.detail.mappingTab.confirmDialog.confirmClosing"
        );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message,
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo")
      },
      width: "500px"
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(res => {
        if (res) {
          this.cardIsLoading = true;
          const data: any = {};
          if (action === "open") {
            data.open = true;
          } else {
            data.open = false;
            // Close item menu opened
            this.mappingIndex.map((item: MappingIndex) => {
              item.content.map((subItem: MappingIndex) => {
                subItem.expanded = false;
              });
              item.expanded = false;
            });
          }
          this.subscriptions.push(
            this.condoStructuresService
              .update(this.condoStructure._id, data)
              .subscribe(res => {
                if (res.status) {
                  this.toastrService.success(res.message);
                  this.cardIsLoading = false;
                  this.condoStructure.open = data.open;
                  this.mappaturaOpen = this.condoStructure.open;
                  if (data.open && this.form) {
                    this.options.formState = {
                      disabled: false
                    };
                    this.formBtnOptions.disabled = false;
                    this.enableFormModify = true;
                  }
                  if (!data.open && this.form) {
                    this.options.formState = {
                      disabled: true
                    };
                    this.formBtnOptions.disabled = true;
                    this.enableFormModify = false;
                  }
                  this.router.navigate(
                    [
                      this.lang,
                      "app",
                      "properties",
                      this.property._id,
                      "mapping",
                    ],
                    {
                      replaceUrl: true
                    }
                  );
                } else {
                  this.toastrService.error(res.message);
                  this.cardIsLoading = false;
                }
              },
                err => {
                  this.helperService.handleError(err);
                })
          );
        }
      });
  }

  /**
   * Enable form to edit structure (not for anagrafica)
   *
   * @since 1.0.0
   */
  onEnableEdit(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "properties.structures.textConfimFormModify"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo")
      },
      width: "500px"
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(res => {
        if (res) {
          this.enableFormModify = true;
          if (this.form) {
            this.options.formState = {
              disabled: false
            };
            this.formBtnOptions.disabled = false;
            this.enableFormModify = true;
          }
        }
      });
  }

  /**
   * Open delete plant dialog
   *
   * @since 1.0.0
   */
  onDeleteDialog(): void {
    // With the 3.0.0 archive switched on, the documents come from it (MAP-A1)
    if (isArchiveV3Live()) {
      this.onArchiveV3DeleteDialog();
      return;
    }
    const message = "properties.structures.textConfimFormDeletePlant";
    const docs = this.getDocsFromArchive(this.activeItem.key, this.formActiveSubitemId);
    const dialogRef = this.dialog.open(
      PropertyDetailMappingDeleteDialogComponent,
      {
        data: {
          title: this.translateService.instant("dialogs.confirm.title"),
          message: this.translateService.instant(message),
          docs,
          propertyId: this.propertyId,
          btnOkText: this.translateService.instant("common.cta.confirm"),
          btnCancelText: this.translateService.instant("common.cta.undo"),
          sectionPlant: this.sectionPlant,
          subscriptionType: this.subscriptionType,
        },
        width: "800px"
      }
    );
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(res => {
        if (res) {
          this.subscriptions.push(
            this.condoStructuresService
              .deleteSection(
                this.activeItem.section,
                this.property.structure,
                this.formActiveSubitemId
              )
              .subscribe(resDel => {
                if (resDel.status) {
                  this.toastrService.success(resDel.message);

                  this.router.navigate(
                    [
                      this.lang,
                      "app",
                      "properties",
                      this.property._id,
                      "mapping",
                    ],
                    {
                      replaceUrl: true
                    }
                  );
                } else {
                  this.toastrService.error(resDel.message);
                }
              })
          );
        }
      });
  }

  /**
   * Get docs from archive per structure
   *
   * @since 1.0.0
   */
  getDocsFromArchive(itemKey: string, itemId: string): any {
    let docs = {};
    switch (itemKey) {
      case "fabbricatoAbitativo":

        break;
      case "struttureFabbindip":

        break;
      case "compartimentoBox":

        break;
      case "portineriaGuardiola":

        break;
      case "appartCustode":

        break;
      case "giardini":

        break;
      case "cortili":

        break;
      case "unitaAbitativaCommerciale":

        break;
      case "unitaImmIndipendente":

        break;
      case "gruppoElettrogeno":
        if (this.documentArchive.impeleGruppoElettrogeno
          && Array.isArray(this.documentArchive.impeleGruppoElettrogeno)) {
          docs = this.documentArchive.impeleGruppoElettrogeno.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "fotovoltaico":
        if (this.documentArchive.impeleFotovoltaico
          && Array.isArray(this.documentArchive.impeleFotovoltaico)) {
          docs = this.documentArchive.impeleFotovoltaico.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "scaricheAtmosferiche":
        if (this.documentArchive.impelescaricheAtmosferiche
          && Array.isArray(this.documentArchive.impelescaricheAtmosferiche)) {
          docs = this.documentArchive.impelescaricheAtmosferiche.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "centraleIdricaPropria":
        if (this.documentArchive.tratAcquaCentraleIdrica
          && Array.isArray(this.documentArchive.tratAcquaCentraleIdrica)) {
          docs = this.documentArchive.tratAcquaCentraleIdrica.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "contatoriAcqua":
        if (this.documentArchive.tratAcquaContatoreAcqua
          && Array.isArray(this.documentArchive.tratAcquaContatoreAcqua)) {
          docs = this.documentArchive.tratAcquaContatoreAcqua.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "sottostazioneAltroCondBsc":
        if (this.documentArchive.tratAcquaSottAlimDaBsc
          && Array.isArray(this.documentArchive.tratAcquaSottAlimDaBsc)) {
          docs = this.documentArchive.tratAcquaSottAlimDaBsc.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "sollevamAcquePiovNere":
        if (this.documentArchive.tratAcquaPompSollacqpiov
          && Array.isArray(this.documentArchive.tratAcquaPompSollacqpiov)) {
          docs = this.documentArchive.tratAcquaPompSollacqpiov.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "contatoreGas":
        if (this.documentArchive.riscaldgasMetcontatrete
          && Array.isArray(this.documentArchive.riscaldgasMetcontatrete)) {
          docs = this.documentArchive.riscaldgasMetcontatrete.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "serbatoiAGasolio":
        if (this.documentArchive.serbatoioAGasolio
          && Array.isArray(this.documentArchive.serbatoioAGasolio)) {
          docs = this.documentArchive.serbatoioAGasolio.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "centraleTermica":
        if (this.documentArchive.riscaldrisccentraliz
          && Array.isArray(this.documentArchive.riscaldrisccentraliz)) {
          docs = this.documentArchive.riscaldrisccentraliz.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "cannaFumaria":
        if (this.documentArchive.riscaldcanneFumariePerCt
          && Array.isArray(this.documentArchive.riscaldcanneFumariePerCt)) {
          docs = this.documentArchive.riscaldcanneFumariePerCt.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "sottostazioniCt":
        if (this.documentArchive.riscaldsottostazriscaldamen
          && Array.isArray(this.documentArchive.riscaldsottostazriscaldamen)) {
          docs = this.documentArchive.riscaldsottostazriscaldamen.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "solareTermico":
        if (this.documentArchive.riscaldsolareTermico
          && Array.isArray(this.documentArchive.riscaldsolareTermico)) {
          docs = this.documentArchive.riscaldsolareTermico.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "teleriscaldamento":
        if (this.documentArchive.riscaldcentrteleriscaldamen
          && Array.isArray(this.documentArchive.riscaldcentrteleriscaldamen)) {
          docs = this.documentArchive.riscaldcentrteleriscaldamen.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "geotermicoConPozzi":
        if (this.documentArchive.riscGeotermicoPozzi
          && Array.isArray(this.documentArchive.riscGeotermicoPozzi)) {
          docs = this.documentArchive.riscGeotermicoPozzi.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "geotermicoConSonde":
        if (this.documentArchive.riscGeotermicoSonde
          && Array.isArray(this.documentArchive.riscGeotermicoSonde)) {
          docs = this.documentArchive.riscGeotermicoSonde.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "pompeDiCalore":
        if (this.documentArchive.riscaldsoloPompeDiCalore
          && Array.isArray(this.documentArchive.riscaldsoloPompeDiCalore)) {
          docs = this.documentArchive.riscaldsoloPompeDiCalore.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "centraleRicervimento":
        if (this.documentArchive.riscaldcentrRicevBsc
          && Array.isArray(this.documentArchive.riscaldcentrRicevBsc)) {
          docs = this.documentArchive.riscaldcentrRicevBsc.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "climatizzazione":
        if (this.documentArchive.riscaldimpcondizionamento
          && Array.isArray(this.documentArchive.riscaldimpcondizionamento)) {
          docs = this.documentArchive.riscaldimpcondizionamento.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "estintori":
        if (this.documentArchive.incendioEstintori
          && Array.isArray(this.documentArchive.incendioEstintori)) {
          docs = this.documentArchive.incendioEstintori.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "reteIdranti":
        if (this.documentArchive.incendioReteIdranti
          && Array.isArray(this.documentArchive.incendioReteIdranti)) {
          docs = this.documentArchive.incendioReteIdranti.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "reteSprinkler":
        if (this.documentArchive.incendioReteSprinkler
          && Array.isArray(this.documentArchive.incendioReteSprinkler)) {
          docs = this.documentArchive.incendioReteSprinkler.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "stazionePompaggio":
        if (this.documentArchive.incendioStazionePompaggio
          && Array.isArray(this.documentArchive.incendioStazionePompaggio)) {
          docs = this.documentArchive.incendioStazionePompaggio.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "rilevazioneFumi":
        if (this.documentArchive.incendioRilevazione
          && Array.isArray(this.documentArchive.incendioRilevazione)) {
          docs = this.documentArchive.incendioRilevazione.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "ventilazione":
        if (this.documentArchive.incendioVentilazione
          && Array.isArray(this.documentArchive.incendioVentilazione)) {
          docs = this.documentArchive.incendioVentilazione.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "evac":
        if (this.documentArchive.incendioEvac
          && Array.isArray(this.documentArchive.incendioEvac)) {
          docs = this.documentArchive.incendioEvac.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "ascensore":
        if (this.documentArchive.ascensoriEMontautoAscensori
          && Array.isArray(this.documentArchive.ascensoriEMontautoAscensori)) {
          docs = this.documentArchive.ascensoriEMontautoAscensori.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "montauto":
        if (this.documentArchive.ascensoriEMontautoMontauto
          && Array.isArray(this.documentArchive.ascensoriEMontautoMontauto)) {
          docs = this.documentArchive.ascensoriEMontautoMontauto.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "tvTerrestre":
        if (this.documentArchive.impElettronicitvTerrestre
          && Array.isArray(this.documentArchive.impElettronicitvTerrestre)) {
          docs = this.documentArchive.impElettronicitvTerrestre.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "tvSatellitare":
        if (this.documentArchive.impElettronicitvSatellitare
          && Array.isArray(this.documentArchive.impElettronicitvSatellitare)) {
          docs = this.documentArchive.impElettronicitvSatellitare.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "citoVideo":
        if (this.documentArchive.impElettroniciimpCitofonico
          && Array.isArray(this.documentArchive.impElettroniciimpCitofonico)) {
          docs = this.documentArchive.impElettroniciimpCitofonico.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "tvCc":
        if (this.documentArchive.impElettroniciimpiantoTvcc
          && Array.isArray(this.documentArchive.impElettroniciimpiantoTvcc)) {
          docs = this.documentArchive.impElettroniciimpiantoTvcc.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "automazCancelli":
        if (this.documentArchive.impElettroniciautomazioni
          && Array.isArray(this.documentArchive.impElettroniciautomazioni)) {
          docs = this.documentArchive.impElettroniciautomazioni.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "impAntintrusione":
        if (this.documentArchive.impElettroniciantintrusione
          && Array.isArray(this.documentArchive.impElettroniciantintrusione)) {
          docs = this.documentArchive.impElettroniciantintrusione.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "lineeVita":
        if (this.documentArchive.lineaVita
          && Array.isArray(this.documentArchive.lineaVita)) {
          docs = this.documentArchive.lineaVita.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "cannaCollettiva":
        if (this.documentArchive.risccanneFumarieColl
          && Array.isArray(this.documentArchive.risccanneFumarieColl)) {
          docs = this.documentArchive.risccanneFumarieColl.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "amianto":

        break;
      case "dipendenti":

        break;
      case "impresePulizia":

        break;
      case "impiantoSpeciale":
        if (this.documentArchive.impiantiSpeciali
          && Array.isArray(this.documentArchive.impiantiSpeciali)) {
          docs = this.documentArchive.impiantiSpeciali.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "attivitavvf":

        break;
      case "piscina":
        if (this.documentArchive.impricreativipiscina
          && Array.isArray(this.documentArchive.impricreativipiscina)) {
          docs = this.documentArchive.impricreativipiscina.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "palestra":
        if (this.documentArchive.impricreativipalestra
          && Array.isArray(this.documentArchive.impricreativipalestra)) {
          docs = this.documentArchive.impricreativipalestra.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "campoTennis":
        if (this.documentArchive.impricreativicampoTennis
          && Array.isArray(this.documentArchive.impricreativicampoTennis)) {
          docs = this.documentArchive.impricreativicampoTennis.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "campoCalcio":
        if (this.documentArchive.impricreativicampoCalcio
          && Array.isArray(this.documentArchive.impricreativicampoCalcio)) {
          docs = this.documentArchive.impricreativicampoCalcio.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
      case "parcoGiochi":
        if (this.documentArchive.impricreativiparcoGiochi
          && Array.isArray(this.documentArchive.impricreativiparcoGiochi)) {
          docs = this.documentArchive.impricreativiparcoGiochi.find((el: any) => {
            return itemId === el.structureObjectId;
          })
        }
        break;
    }

    // Fallback for plant types that are not explicitly mapped in the switch
    // above (e.g. custom/dynamic plants defined in the backend structure
    // template, such as "addolcitore..." water softeners). Every document
    // archive bucket is an array whose entries carry the structureObjectId of
    // the structure item they belong to, so when no specific case resolved the
    // documents we can look them up generically by scanning every array bucket
    // for the entry matching this structure item id.
    const noSpecificMatch = !docs || Object.keys(docs).length === 0;
    if (noSpecificMatch && this.documentArchive) {
      const archiveKeys = Object.keys(this.documentArchive);
      for (const archiveKey of archiveKeys) {
        const bucket = this.documentArchive[archiveKey];
        if (Array.isArray(bucket)) {
          const match = bucket.find((el: any) => el && el.structureObjectId === itemId);
          if (match) {
            docs = match;
            break;
          }
        }
      }
    }

    if (docs) {
      const keys = Object.keys(docs);
      if (keys.includes("documents")) {
        const obj = docs["documents"];
        for (const key in obj) {
          if (Array.isArray(obj[key]) && obj[key].length === 0) {
            delete obj[key];
          } else if (Array.isArray(obj[key]) && obj[key].length) {
            // tradurre ID22 ?
            // cancellare doc scaduti || el.expired
            // cancellare doc eliminati
            // cancellare doc che non hanno data
            obj[key].forEach((el: any) => {
              if (el.deleted || !el.data) {
                delete obj[key];
              }
            })
          }
        }
      }
    }

    return docs;
  }

  /**
   * Open remake plant dialog
   *
   * @since 1.0.0
   */
  onRemakeDialog(): void {
    // With the 3.0.0 archive switched on, the documents come from it (MAP-A1)
    if (isArchiveV3Live()) {
      this.onArchiveV3RemakeDialog();
      return;
    }
    const message = "properties.structures.textConfimFormRemake";
    const docs = this.getDocsFromArchive(this.activeItem.key, this.formActiveSubitemId);
    const dialogRef = this.dialog.open(
      PropertyDetailMappingRemakeDialogComponent,
      {
        data: {
          title: this.translateService.instant("dialogs.confirm.title"),
          message: this.translateService.instant(message),
          docs,
          propertyId: this.propertyId,
          btnOkText: this.translateService.instant("common.cta.confirm"),
          btnCancelText: this.translateService.instant("common.cta.undo"),
          subscriptionType: this.subscriptionType,
        },
        width: "800px"
      }
    );
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(res => {
        if (res) {
          this.subscriptions.push(
            this.condoStructuresService
              .deleteSection(
                this.activeItem.section,
                this.property.structure,
                this.formActiveSubitemId
              )
              .subscribe(resDel => {
                if (resDel.status) {
                  this.toastrService.success(resDel.message);
                  // @todo reload struttura
                  if (typeof docs !== "undefined") {
                    this.condoStructuresService.setRemakeStrucutureDocs(this.formActiveSection, docs._id, res)
                  }
                  this.activateSection(this.formActiveSection);
                } else {
                  this.toastrService.error(resDel.message);
                }
              })
          );
        }
      });
  }

  /**
   * «Elimina impianto» / «Elimina la scheda» with the 3.0.0 archive switched on (MAP-A1): the
   * confirmation lists the documents of the element from the new archive; the deletion is the one of
   * @see onDeleteDialog.
   *
   * @since 3.0.0
   */
  private onArchiveV3DeleteDialog(): void {
    // The element of the dialog: read now, not when the dialog answers
    const rawSection = this.activeItem.section;
    const elementId = this.formActiveSubitemId;
    const dialogRef = this.dialog.open<
      MappingArchiveDocsDialogComponent,
      MappingArchiveDocsDialogData,
      MappingArchiveDocsDialogResult
    >(MappingArchiveDocsDialogComponent, {
      data: this.archiveV3DocsDialogData("delete", elementId),
      width: "800px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(res => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.condoStructuresService
            .deleteSection(rawSection, this.property.structure, elementId)
            .subscribe(
              resDel => {
                if (resDel.status) {
                  this.toastrService.success(resDel.message);
                  this.router.navigate(
                    [this.lang, "app", "properties", this.property._id, "mapping"],
                    { replaceUrl: true }
                  );
                } else {
                  this.toastrService.error(resDel.message);
                }
              },
              err => this.helperService.handleError(err)
            )
        );
      });
  }

  /**
   * «Rifare impianto» with the 3.0.0 archive switched on (MAP-A1): the dialog lists the documents of
   * the plant from the new archive and hands back the ticked rows. After the deletion of
   * @see onRemakeDialog the selection is kept for the new plant, and the POST that creates it
   * (@see onFormSubmit) sends it as `remakePlantDocs`: `sourceStructureId` and
   * `documentArchiveDocIds`. The rows are read BEFORE the deletion, which withdraws them.
   *
   * @since 3.0.0
   */
  private onArchiveV3RemakeDialog(): void {
    // The plant being remade: read now, the active form changes once it is deleted
    const section = this.formActiveSection;
    const rawSection = this.activeItem.section;
    const sourceStructureId = this.formActiveSubitemId;
    const dialogRef = this.dialog.open<
      MappingArchiveDocsDialogComponent,
      MappingArchiveDocsDialogData,
      MappingArchiveDocsDialogResult
    >(MappingArchiveDocsDialogComponent, {
      data: this.archiveV3DocsDialogData("remake", sourceStructureId),
      width: "800px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(res => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.condoStructuresService
            .deleteSection(rawSection, this.property.structure, sourceStructureId)
            .subscribe(
              resDel => {
                if (resDel.status) {
                  this.toastrService.success(resDel.message);
                  this.condoStructuresService.setRemakePlantSelection(
                    section,
                    remakePlantSelection(sourceStructureId, res.documentArchiveDocIds)
                  );
                  this.activateSection(section);
                } else {
                  this.toastrService.error(resDel.message);
                }
              },
              err => this.helperService.handleError(err)
            )
        );
      });
  }

  /**
   * What the dialog of the 3.0.0 archive needs to list the documents of one element (MAP-A1).
   *
   * @since 3.0.0
   */
  private archiveV3DocsDialogData(
    mode: MappingArchiveDocsDialogMode,
    elementId: string
  ): MappingArchiveDocsDialogData {
    return {
      mode,
      documentArchiveId: this.property ? this.property.documentArchive : "",
      structureObjectId: elementId,
      sectionPlant: this.sectionPlant,
    };
  }

  // /**
  //  * Open note dialog
  //  *
  //  * @since 1.0.0
  //  */
  // onNoteDialog(): void {
  //   const dialogRef = this.dialog.open(
  //     PropertyDetailMappingNoteDialogComponent,
  //     {
  //       data: {
  //         modalTitle: this.translateService.instant(
  //           "properties.structures.notes"
  //         ),
  //         existingNotes: this.existingNotes,
  //         condoStructureId: this.condoStructure._id,
  //         structureId: this.model._id,
  //         structureName: this.model.nome,
  //       },
  //       width: "500px"
  //     }
  //   );
  // }

  /**
   * Export properties in a file with given format
   *
   * @since 1.0.0
   */
  onExport(target?: string, propertyId?: string): void {
    // With the switch live, the two Excels of the property come from the new archive (NEW-M7)
    const archiveV3Target = isArchiveV3Live() ? documentArchiveExportTargetOf(target) : null;
    if (archiveV3Target !== null) {
      this.exportFromArchiveV3(archiveV3Target);
      return;
    }

    // If target is not provided, than just export all properties
    if (!target) {
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        "properties.list.exportFileName"
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getExport().subscribe(
          res => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel"
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    } else if (target && typeof propertyId !== "undefined") {
      // Handle export for missing/inserted documents
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        `properties.list.${target}`
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getDocExport(target, propertyId).subscribe(
          res => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel"
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }

  /**
   * «Esporta inseriti» and «Esporta mancanti» with the switch live: the Excel of the new archive, as
   * the «Esporta» menu of its dossier gives it. One download at a time; the entry is checked again
   * here and not only in the template, as the dossier does: a tab left open across a role change must
   * not ask for an Excel the new role is not shown.
   *
   * @since 3.0.0
   *
   * @param target Which Excel
   */
  private exportFromArchiveV3(target: DocumentArchiveExportTarget): void {
    if (this.exportingTarget !== null || !this.propertyId || !this.downloadMenu[target]) {
      return;
    }
    this.exportingTarget = target;
    if (this.exportSubscription) {
      this.exportSubscription.unsubscribe();
    }
    this.exportSubscription = this.documentArchiveExportDownloadService
      .download(target, this.propertyId)
      .pipe(
        finalize(() => {
          this.exportingTarget = null;
        })
      )
      .subscribe();
  }

  /**
   * Prevent redirect to archive if structure is open
   *
   * @since 1.0.0
   */
  onGoToDocumentArchive(): void {
    if (!this.mappaturaOpen) {
      return;
    }
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.mappaturaOpen.title'),
        message: this.translateService.instant('dialogs.confirm.mappaturaOpen.message'),
        btnCancelText: this.translateService.instant('common.cta.close'),
        messageOnly: true,
      },
      width: '500px',
      disableClose: false,
    });
    // // Subscribe to dialog result (only for 1 emit thanks to "take()")
    // dialogRef.beforeClosed()
    //   .pipe(
    //     take(1)
    //   )
    //   .subscribe(
    //     res2 => {
    //       if (res2) {
    //         // User hit confirm button. We can redirect him
    //         this.router.navigate([this.lang, 'app', 'properties', this.propertyId, 'documents']);
    //       }
    //     }
    //   );
  }

  
  /**
   * Handle click to show document notes
   *
   * @since 1.0.0
   */
   onViewDocumentNotes(): void {
    const note = [];

    if (this.condoStructure) {
      for (const key in this.condoStructure) {
        if (
          Array.isArray(this.condoStructure[key]) &&
          this.condoStructure[key].length
        ) {

          let structureTranslated = '';
          let find = false;
          const structureTemplate = JSON.parse(JSON.stringify(this.condoStructureTemplate));
          if (structureTemplate && Array.isArray(structureTemplate)) {
            for (const level1 of structureTemplate) {
              if (level1.content && Array.isArray(level1.content)) {
                for (const level2 of level1.content) {
                  // if (level2.title === `properties.structures.${key}`) {
                  if (level2.key === key) {
                    structureTranslated = level2.translation[this.lang];
                    find = true;
                    break;
                  }
                }
              }
              if (find) {
                break;
              }
            }
          }
          
          this.condoStructure[key].forEach((el: any, index: number) => {
            if (el.active && el.note && Array.isArray(el.note) && el.note.length) {
              el.note.forEach((subEl: any, indexNote: number) => {
                note.push({
                  createdAt: subEl.createdAt,
                  createdBy:
                    subEl.createdBy.firstName + " " + subEl.createdBy.lastName,
                  text: subEl.text,
                  name: el.nome ? el.nome : "",
                  // structure: this.translateService.instant(
                  //   "properties.structures." + key
                  // ),
                  structure: structureTranslated,
                  structureName: key,
                  structurePosition: index,
                  structureId: el._id,
                  notePosition: indexNote,
                  noteId: subEl._id,
                });
              });
            }
          });
        }
      }
    }

    const noteDialogRef = this.dialog.open(
      PropertyDetailDocumentNoteDialogComponent,
      {
        data: {
          modalTitle: this.translateService.instant(
            "properties.documentsPage.noteList"
          ),
          condoStructureId: this.condoStructure._id,
          notes: note,
        },
        minWidth: "50%",
      }
    );
    // noteDialogRef
    //   .beforeClosed()
    //   .pipe(take(1))
    //   .subscribe((res) => {
    //     if (res && res.reload) {
    //       this.getStructureData();
    //     }
    //   });
  }

  /**
   * Word contracts export
   *
   * @since 1.0.0
   */
  onExportContracts(): void {
    // With the switch live, the contracts come from the new archive, in their own dialog (FE-M8)
    if (isArchiveV3Live()) {
      if (this.propertyId && this.downloadMenu.contracts) {
        openDocumentArchiveExportContractsDialog(this.dialog, this.propertyId);
      }
      return;
    }

    const dialogRef = this.dialog.open(ExportContractsDialogComponent, {
      minWidth: "50%",
      maxHeight: "98vh",
      data: {
        propertyId: this.propertyId,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        return;
      });
  }
  
  /**
   * RAS docs export in PDF format
   *
   * @since 1.0.0
   */
  onExportRAS(): void {
    // With the switch live, the RAS comes from the new archive, in its own dialog (FE-M8)
    if (isArchiveV3Live()) {
      if (this.propertyId && this.downloadMenu.ras) {
        openDocumentArchiveExportRasDialog(this.dialog, this.propertyId);
      }
      return;
    }

    const dialogRef = this.dialog.open(ExportRasDocsDialogComponent, {
      width: "700px",
      data: {
        propertyId: this.propertyId,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        return;
      });
  }

    
  /**
   * Handle click open QRcode dialogbox
   *
   * @since 1.0.0
   */
  onPrintQRcode(): void {
    this.dialog.open(PrintQrcodeDialogComponent, {
      data: {
        title: this.translateService.instant("properties.detail.mappingTab.qrcodeDialog.title"),
        subtitle: this.translateService.instant("properties.detail.mappingTab.qrcodeDialog.subtitle"),
        propertyName: this.property.name,
        qrCode: this.mappingQrcodeData,
        name: this.companyData.name,
        city: this.companyData.legalAddress.city,
        address: `${this.companyData.legalAddress.route} ${this.companyData.legalAddress.streetNumber}`
      },
      width: "700px",
    });
  }
  
  /**
   * Handle Qrcode send by email
   */
  onSendQrcodeByEmail(): void {
    const qrImage = document
      .getElementById("mappingQrcodeDataImage")
      .lastElementChild.lastElementChild.getAttribute("src");

    // Open dialog
    const newMessageDialogRef = this.dialog.open(
      AddCommunicationDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          body: this.translateService.instant("properties.detail.mappingTab.linkViewMapping", { link: this.mappingQrcodeData }),
          qrImage,
          phonebook: this.companyData.phonebook,
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  /**
   * Handle edit
   *
   * @since 1.0.0
   */
  onEdit(
    text: string,
    noteId: string
  ): void {
    const editDialogRef = this.dialog.open(
      PropertyEditNoteDialogComponent,
      {
        minWidth: "40%",
        maxHeight: "98vh",
        data: {
          title: this.translateService.instant(
            "properties.structures.notes"
          ),
          condoStructureId: this.condoStructure._id,
          text,
          structureName: this.model.nome ? this.model.nome : "Anagrafica",
          structureId: this.model._id,
          noteId,
        },
        disableClose: true,
      }
    );
    editDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res || !res.status) {
          return;
        }
        const index = this.existingNotes.findIndex((el: any) => {
          return (
            el._id.toString() === noteId.toString()
          );
        });
        if (index > -1) {
          this.existingNotes[index].text = res.text;
        }
        return;
      });
  }

  /**
   * Handle delete
   *
   * @since 1.0.0
   */
  onDelete(noteId: string): void {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      minWidth: "30%",
      maxHeight: "98vh",
      data: {
        title: this.translateService.instant("common.warnings.warning"),
        message: this.translateService.instant("common.warnings.noteDelete"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      disableClose: true,
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    confirmDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.condoStructuresService
            .deleteNote(
              this.condoStructure._id,
              this.model.nome,
              this.model._id,
              noteId
            )
            .pipe(take(1))
            .subscribe(
              (res) => {
                if (res.status) {
                  this.toastrService.success(res.message);
                  const index = this.existingNotes.findIndex((el: any) => {
                    return (
                      el._id.toString() === noteId.toString()
                    );
                  });
                  if (index > -1) {
                    this.existingNotes.splice(index, 1);
                  }
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
              }
            )
        );
        return;
      });
  }

  /**
   * Open a modal window to display the mapping of all open elements
   */
  onViewMapping(): void {
    this.dialog.open(PropertyDetailMappingViewDialogComponent, {
      width: '800px',
      data: {
        structureId: this.condoStructure._id,
      },
      disableClose: true,
    });
  }
}
